import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DateField } from '@/components/ui/date-time-field';
import { RemoteSelect } from '@/components/ui/remote-select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { assetProcurementApi } from '@/lib/assetProcurementApi';
import { itApi } from './itApi';
import { todayJst } from '@/utils/jstDate';

const today = () => todayJst();

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-xs font-semibold text-zinc-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

/**
 * IT消耗品の入庫（受け入れ）。まとめ売りの品は「2セット = 60本」で入れられる。
 * 在庫は必ず基本単位で保存する（画面の単位トグルは入力の都合だけ）。
 */
export default function ConsumableReceiptDialog({ open, onClose, asset, onSaved }) {
  const packQty = Number(asset?.pack_qty) || 0;
  const packUnit = asset?.purchase_unit || 'セット';
  const baseUnit = asset?.unit || '個';
  const [byPack, setByPack] = useState(false);
  const [qty, setQty] = useState('');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setByPack(packQty > 0);
    setQty('');
    setForm({
      received_date: today(),
      unit_price: '', pack_price: asset?.pack_price ?? '',
      supplier_id: asset?.supplier_id || '', supplier_name: asset?.supplier_name || '',
      source_request_no: '', techs_order_no: '', received_by: '',
      location: asset?.location || '', note: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset?.id]);

  const entered = Number(qty) || 0;
  const baseQty = byPack && packQty > 0 ? entered * packQty : entered;
  const preview = byPack && packQty > 0 && entered > 0
    ? `${entered}${packUnit} = ${baseQty}${baseUnit}` : null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async () => {
    if (!(entered > 0)) { toast.error('入庫数を入力してください'); return; }
    setSaving(true);
    try {
      const num = (v) => (v === '' || v == null ? null : Number(v));
      const r = await itApi.createReceipt(asset.id, {
        qty: entered,
        unit_mode: byPack && packQty > 0 ? 'pack' : 'base',
        received_date: form.received_date || today(),
        unit_price: num(form.unit_price),
        pack_price: num(form.pack_price),
        supplier_id: form.supplier_id || null,
        supplier_name: form.supplier_name || null,
        source_request_no: form.source_request_no || null,
        techs_order_no: form.techs_order_no || null,
        received_by: form.received_by || null,
        location: form.location || null,
        note: form.note || null,
      });
      toast.success(`入庫を記録しました（在庫 ${r.asset?.on_hand_qty ?? ''}${baseUnit}）`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '入庫の記録に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}
        data-testid="consumable-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={18} weight="duotone" className="text-purple-600" />
            入庫を記録
          </DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-xs text-zinc-500" data-no-translate>
          {asset?.name}{asset?.pack_label ? `（${asset.pack_label}）` : ''}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="入庫数" testid="receipt-field-qty">
            <div className="flex items-center gap-2">
              <Input data-testid="receipt-input-qty" type="number" min="0" step="any" value={qty}
                onChange={(e) => setQty(e.target.value)} className="h-10 w-28 tabular-nums" />
              {packQty > 0 ? (
                <span className="inline-flex items-center rounded-md bg-zinc-100 p-1" data-testid="receipt-unit-toggle">
                  <button type="button" onClick={() => setByPack(false)} data-testid="receipt-unit-base"
                    className={`rounded px-2.5 py-1 text-xs ${!byPack ? 'bg-white font-semibold text-purple-700 shadow-sm' : 'text-zinc-500'}`}>
                    {baseUnit}
                  </button>
                  <button type="button" onClick={() => setByPack(true)} data-testid="receipt-unit-pack"
                    className={`rounded px-2.5 py-1 text-xs ${byPack ? 'bg-white font-semibold text-purple-700 shadow-sm' : 'text-zinc-500'}`}>
                    {packUnit}
                  </button>
                </span>
              ) : <span className="text-xs text-zinc-500">{baseUnit}</span>}
            </div>
            {preview && (
              <p className="mt-1 text-[11px] font-semibold text-emerald-800" data-testid="receipt-pack-preview">
                {preview} として記録します
              </p>
            )}
          </Field>
          <Field label="入庫日" testid="receipt-field-date">
            <DateField testId="receipt-input-date" value={form.received_date || ''}
              onChange={set('received_date')} inputClassName="h-10" />
          </Field>
          {packQty > 0 && (
            <Field label={`パック価格（¥ / 1${packUnit}）`} testid="receipt-field-pack-price">
              <Input data-testid="receipt-input-pack-price" type="number" min="0"
                value={form.pack_price ?? ''} onChange={set('pack_price')} className="h-10 tabular-nums" />
              <p className="mt-0.5 text-[10px] text-zinc-400">単価が空ならパック価格÷入数で計算します</p>
            </Field>
          )}
          <Field label={`単価（¥ / 1${baseUnit}）`} testid="receipt-field-unit-price">
            <Input data-testid="receipt-input-unit-price" type="number" min="0"
              value={form.unit_price ?? ''} onChange={set('unit_price')} className="h-10 tabular-nums" />
          </Field>
          <Field label="購入先" testid="receipt-field-supplier">
            <RemoteSelect
              fetch={assetProcurementApi.suppliers}
              value={form.supplier_id || null}
              onChange={(o) => setForm((f) => ({ ...f, supplier_id: o?.id || '', supplier_name: o?.name || '' }))}
              placeholder="仕入先マスターから選択…" allowClear testId="receipt-input-supplier"
            />
            <Input value={form.supplier_id ? '' : (form.supplier_name || '')}
              onChange={set('supplier_name')} disabled={!!form.supplier_id}
              placeholder="マスター未登録の店はここに入力" className="mt-1 h-9 text-xs"
              data-testid="receipt-input-supplier-free" />
          </Field>
          <Field label="REQ-No." testid="receipt-field-req">
            <Input data-testid="receipt-input-req" value={form.source_request_no || ''}
              onChange={set('source_request_no')} placeholder="例: REQ-2026-0123"
              className="h-10 font-mono text-sm" />
          </Field>
          <Field label="TECHS受注No." testid="receipt-field-techs">
            <Input data-testid="receipt-input-techs" value={form.techs_order_no || ''}
              onChange={set('techs_order_no')} className="h-10 font-mono text-sm" />
          </Field>
          <Field label="受領者" testid="receipt-field-receiver">
            <Input data-testid="receipt-input-receiver" value={form.received_by || ''}
              onChange={set('received_by')} placeholder="空欄なら自分の名前" className="h-10" />
          </Field>
          <Field label="入庫先（保管場所）" testid="receipt-field-location">
            <Input data-testid="receipt-input-location" value={form.location || ''}
              onChange={set('location')} className="h-10" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="メモ" testid="receipt-field-note">
              <Input data-testid="receipt-input-note" value={form.note || ''} onChange={set('note')} className="h-10" />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="receipt-cancel">キャンセル</Button>
          <Button onClick={submit} disabled={saving} className="bg-purple-600 hover:bg-purple-700"
            data-testid="receipt-submit">{saving ? '記録中…' : '入庫を記録する'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
